import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Grid,
  Divider,
  Paper,
  Collapse,
} from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import { ForkLeft } from "@mui/icons-material";
import { useRef } from "react";

/* ================= CONSTANTS ================= */

const VARIANT_OPTIONS = ["4.4kWh", "3.4kWh", "2.6kWh"];

const PRODUCT_OPTIONS = ["Rorr Ez Sigma", "Rorr Ez", "Rorr"];

const SPEC_FIELDS = [
  { label: "Battery Capacity", key: "batteryCapacity" },
  { label: "Speed", key: "speed" },
  { label: "Range", key: "range" },
  { label: "Charging Time", key: "chargingTime" },
  { label: "Display", key: "display" },
  { label: "Reverse Mode", key: "reverseMode" },
  { label: "Colours", key: "colours" },
  { label: "Standard Warranty", key: "warranty" },
];

const PRICE_FIELDS = [
  { label: "Ex-showroom Price", key: "exShowroom" },
  { label: "Offer Price", key: "onRoad" },
];

/* ================= COMPONENT ================= */

export default function ProductvariantBanner({ title, products, setProducts }) {
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  const [showSpecs, setShowSpecs] = useState(false);

  const [showPrice, setShowPrice] = useState(false);
  const [reverseModeError, setReverseModeError] = useState("");

  const specsRef = useRef(null);
  const priceRef = useRef(null);

  const [formData, setFormData] = useState({
    variant: "",
    productName: "",
    title: "",
    description: "",
  });
  const [errors, setErrors] = useState({
    variant: "",
    productName: "",
    title: "",
    description: "",
  });

  const [specification, setSpecification] = useState({});
  const [price, setPrice] = useState({});
  const [colourError, setColourError] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [priceErrors, setPriceErrors] = useState({});

  const isOnlyText = (value) => /^[A-Za-z]*$/.test(value);

  /* ================= ADD / UPDATE ================= */

  const handleSubmit = () => {
    // if (!formData.variant || !formData.productName) return;
    const newErrors = {
      variant: "",
      productName: "",
      title: "",
      description: "",
    };

    if (!formData.variant) newErrors.variant = "Variant is required";
    // if (!formData.productName.trim())
    if (!formData.productName)
      newErrors.productName = "Product name is required";
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.description.trim())
      newErrors.description = "Description is required";

    setErrors(newErrors);

    // ❌ Stop submit if any error exists
    if (Object.values(newErrors).some((e) => e)) return;

    const newPriceErrors = {};

    if (showPrice) {
      PRICE_FIELDS.forEach((f) => {
        if (!price[f.key] || !price[f.key].trim()) {
          newPriceErrors[f.key] = `${f.label} is required`;
        }
      });

      // 🔥 CROSS FIELD VALIDATION
      // const priceLogicError = validatePriceLogic(price);
      // if (priceLogicError) {
      //   newPriceErrors.onRoad = priceLogicError;
      // }
    }

    setPriceErrors(newPriceErrors);

    // ❌ Stop submit if price errors exist
    if (Object.keys(newPriceErrors).length) return;

    const payload = {
      variant: formData.variant,
      productName: formData.productName,
      title: formData.title,
      description: formData.description,
    };

    // ✅ clone specification
    if (showSpecs && Object.keys(specification).length) {
      payload.specification = { ...specification };
    }

    // ✅ clone price
    if (showPrice && Object.keys(price).length) {
      payload.price = { ...price };
    }

    if (isEdit) {
      // ✅ UPDATE BY ID
      setProducts((prev) =>
        prev.map((p) =>
          p.id === editId
            ? {
                ...p,
                ...payload,
                specification: payload.specification
                  ? { ...payload.specification }
                  : undefined,
                price: payload.price ? { ...payload.price } : undefined,
                warranty: payload.warranty
                  ? payload.warranty.map((w) => ({ ...w }))
                  : [],
              }
            : p,
        ),
      );
    } else {
      // ✅ ADD WITH UNIQUE ID + DEEP CLONE
      setProducts((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          ...payload,
          specification: payload.specification
            ? { ...payload.specification }
            : undefined,
          price: payload.price ? { ...payload.price } : undefined,
          warranty: payload.warranty
            ? payload.warranty.map((w) => ({ ...w }))
            : [],
        },
      ]);
    }

    resetDialog();
  };

  /* ================= EDIT ================= */

  const handleEdit = (item) => {
    setIsEdit(true);
    setEditId(item.id);

    setFormData({
      variant: item.variant || "",
      productName: item.productName || "",
      title: item.title || "",
      description: item.description || "",
    });

    // ✅ deep clone
    setSpecification(item.specification ? { ...item.specification } : {});
    setPrice(item.price ? { ...item.price } : {});

    setShowSpecs(!!item.specification);
    // setShowWarranty(!!item.warranty?.length);
    setShowPrice(!!item.price);

    setOpen(true);
  };

  /* ================= DELETE ================= */

  // const handleDelete = (id) => {
  //   setProducts((prev) => prev.filter((p) => p.id !== id));
  // };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    setProducts((prev) => prev.filter((p) => p.id !== deleteId));
    setDeleteId(null);
    setOpenDeleteDialog(false);
  };

  const handleCancelDelete = () => {
    setDeleteId(null);
    setOpenDeleteDialog(false);
  };

  /* ================= RESET ================= */

  const resetDialog = () => {
    setOpen(false);
    setIsEdit(false);
    setEditId(null);
    setFormData({ variant: "", productName: "", title: "", description: "" });
    setSpecification({});
    setPrice({});
    // setWarranty({ plan: "", text: "", price: "" });
    setShowSpecs(false);

    setShowPrice(false);
  };

  const editorOptions = {
    buttonList: [
      ["bold", "italic", "underline"],
      ["list", "align"],
      ["link"],
      ["removeFormat"],
    ],
    height: "200px",
  };

  // const validatePriceLogic = (price) => {
  //   const ex = Number(price.exShowroom);
  //   const offer = Number(price.onRoad);

  //   if (!isNaN(ex) && !isNaN(offer)) {
  //     if (offer >= ex) {
  //       return "Offer price must be less than Ex-showroom price";
  //     }
  //   }
  //   return "";
  // };

  /* ================= UI ================= */

  return (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
        <Typography fontWeight={600}>{title}</Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Button
          variant="contained"
          sx={{ mb: 2 }}
          onClick={() => setOpen(true)}
        >
          Add Product
        </Button>

        {products.map((item) => (
          <Paper key={item.id} sx={{ p: 2, mb: 2 }}>
            <Grid container justifyContent="space-between" alignItems="center">
              <Typography fontWeight={600}>
                {item.productName} – {item.variant}
              </Typography>
              <div>
                <IconButton onClick={() => handleEdit(item)}>
                  <EditIcon />
                </IconButton>
                {/* <IconButton onClick={() => handleDelete(item.productName)}> */}
                <IconButton onClick={() => handleDeleteClick(item.id)}>
                  <DeleteIcon />
                </IconButton>
              </div>
            </Grid>

            {/* Specifications */}
            {item.specification && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography fontWeight={600}>Specifications</Typography>
                {SPEC_FIELDS.map(
                  (f) =>
                    item.specification[f.key] && (
                      <Typography key={f.key} variant="body2">
                        <strong>{f.label}:</strong> {item.specification[f.key]}
                      </Typography>
                    ),
                )}
              </>
            )}

            {/* Price */}
            {item.price && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography fontWeight={600}>Price</Typography>
                {PRICE_FIELDS.map(
                  (f) =>
                    item.price[f.key] && (
                      <Typography key={f.key} variant="body2">
                        <strong>{f.label}:</strong> {item.price[f.key]}
                      </Typography>
                    ),
                )}
              </>
            )}
          </Paper>
        ))}

        {/* ================= DIALOG ================= */}

        <Dialog open={open} onClose={resetDialog} fullWidth maxWidth="sm">
          <DialogTitle>{isEdit ? "Edit Product" : "Add Product"}</DialogTitle>

          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Variant</InputLabel>
                  <Select
                    value={formData.variant}
                    label="Variant"
                    onChange={(e) => {
                      setFormData({ ...formData, variant: e.target.value });
                      setErrors((prev) => ({
                        ...prev,
                        variant: "",
                      }));
                    }}
                  >
                    {VARIANT_OPTIONS.map((v) => (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    ))}
                  </Select>

                  {errors.variant && (
                    <Typography color="error" variant="caption">
                      {errors.variant}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.productName}
                >
                  <InputLabel>Product Name</InputLabel>

                  <Select
                    value={formData.productName}
                    label="Product Name"
                    onChange={(e) => {
                      setFormData({ ...formData, productName: e.target.value });
                      setErrors((prev) => ({
                        ...prev,
                        productName: "",
                      }));
                    }}
                  >
                    {PRODUCT_OPTIONS.map((product) => (
                      <MenuItem key={product} value={product}>
                        {product}
                      </MenuItem>
                    ))}
                  </Select>

                  {errors.productName && (
                    <Typography color="error" variant="caption">
                      {errors.productName}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {["title", "description"].map((k) => (
                <Grid item xs={12} key={k}>
                  <TextField
                    fullWidth
                    size="small"
                    label={k}
                    value={formData[k]}
                    error={!!errors[k]}
                    helperText={errors[k]}
                    onChange={(e) => {
                      setFormData({ ...formData, [k]: e.target.value });
                      setErrors({ ...errors, [k]: "" });
                    }}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Toggle Buttons */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={4}>
                {/* <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setShowSpecs(!showSpecs)}
                >
                  {showSpecs ? "Hide Specifications" : "Add Specifications"}
                </Button> */}
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setShowSpecs((prev) => {
                      const next = !prev;
                      if (!prev) {
                        setTimeout(() => {
                          specsRef.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }, 150);
                      }
                      return next;
                    });
                  }}
                >
                  {showSpecs ? "Hide Specifications" : "Add Specifications"}
                </Button>
              </Grid>

              <Grid item xs={12} sm={4}>
                {/* <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setShowPrice(!showPrice)}
                >
                  {showPrice ? "Hide Price" : "Add Price"}
                </Button> */}
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setShowPrice((prev) => {
                      const next = !prev;
                      if (!prev) {
                        setTimeout(() => {
                          priceRef.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }, 150);
                      }
                      return next;
                    });
                  }}
                >
                  {showPrice ? "Hide Price" : "Add Price"}
                </Button>
              </Grid>
            </Grid>

            {/* Specifications */}
            <Collapse in={showSpecs} sx={{ mt: 2 }} ref={specsRef}>
              {SPEC_FIELDS.map((f) => {
                const isColourField = f.key === "colours";
                const isReverseModeField = f.key === "reverseMode";

                return (
                  <TextField
                    key={f.key}
                    fullWidth
                    size="small"
                    sx={{ mb: 1 }}
                    label={f.label}
                    value={specification[f.key] || ""}
                    error={
                      (isColourField && !!colourError) ||
                      (isReverseModeField && !!reverseModeError)
                    }
                    helperText={
                      isColourField
                        ? colourError
                        : isReverseModeField
                          ? reverseModeError
                          : ""
                    }
                    onChange={(e) => {
                      const value = e.target.value;

                      if (isReverseModeField) {
                        if (/\d/.test(value)) {
                          setReverseModeError("Numbers are not allowed");
                          return;
                        } else {
                          setReverseModeError("");
                        }
                      }

                      if (isColourField) {
                        if (!isOnlyText(value)) {
                          setColourError("Only alphabets are allowed");
                          return; // ❌ block update
                        } else {
                          setColourError("");
                        }
                      }

                      setSpecification({
                        ...specification,
                        [f.key]: value,
                      });
                    }}
                  />
                );
              })}
            </Collapse>

            {/* Price */}
            <Collapse in={showPrice} sx={{ mt: 2 }} ref={priceRef}>
              {PRICE_FIELDS.map((f) => (
                <TextField
                  key={f.key}
                  fullWidth
                  size="small"
                  sx={{ mb: 1 }}
                  label={f.label}
                  value={price[f.key] || ""}
                  error={!!priceErrors[f.key]}
                  helperText={priceErrors[f.key]}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  onChange={(e) => {
                    const value = e.target.value;

                    // ❌ Non-numeric input
                    if (!/^\d*$/.test(value)) {
                      setPriceErrors((prev) => ({
                        ...prev,
                        [f.key]: "Please enter numbers only",
                      }));
                      return;
                    }

                    // ✅ Valid numeric input
                    setPriceErrors((prev) => ({
                      ...prev,
                      [f.key]: "",
                    }));

                    setPrice((prev) => ({
                      ...prev,
                      [f.key]: value,
                    }));
                  }}
                />
              ))}
            </Collapse>
          </DialogContent>

          <DialogActions>
            <Button onClick={resetDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {isEdit ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
      </AccordionDetails>
      <Dialog
        open={openDeleteDialog}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Product</DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to delete this product variant?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  );
}
